import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Link,
  SimpleGrid,
  HStack,
  Tooltip,
  Button,
  Badge,
  Progress,
  Select,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  useDisclosure,
  Collapse,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  useToast,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { FiUsers, FiAlertTriangle, FiFilter, FiEye, FiChevronDown, FiEdit3, FiSend } from 'react-icons/fi';
import { MdMarkEmailRead } from 'react-icons/md';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import { BiGridAlt, BiCheckDouble } from 'react-icons/bi';
import SmartFilterBar, { SCHOOL_FILTER_OPTIONS, STATUS_FILTER_OPTIONS, ACTIVE_REPORT_STATUS_OPTION } from 'components/SmartFilterBar';
import SchoolBadge from 'components/SchoolBadge';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';
import dayjs from 'dayjs';
import { downloadExcel } from 'react-export-table-to-excel';

const PAGE_SIZE = 10;
const PASSING_SCORE = 6.5;
const DETAILED_ACADEMIC_EXPORT_TIMEOUT_MS = 600000;
const VISIBLE_REPORT_FILTERS = ['program_type', 'program', 'group', 'status', 'semester'];

/** Sin `status` en la URL: filtrar por matrícula activa; el usuario puede cambiar a «Todos los estados». */
const DEFAULT_STATUS_FILTER = 'activos';

function downloadCertificateFile(blob, filename) {
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
}

const PERIOD_OPTIONS = [
  { value: '', label: 'Todos los periodos' },
  { value: 'primer', label: 'Primer Periodo' },
  { value: 'segundo', label: 'Segundo Periodo' },
  { value: 'tercer', label: 'Tercer Periodo' },
  { value: 'cuarto', label: 'Cuarto Periodo' },
];

const TECHNICAL_CAREER_SLUG = 'carreras-tecnicas';
const SEMESTRALIZADO_SLUG = 'bachillerato-semestralizado-para-adolescentes';

const PERIOD_OPTIONS_TECHNICAL = [
  { value: '', label: 'Todos los periodos' },
  { value: 'P1', label: 'Periodo 1 (P1)' },
  { value: 'P2', label: 'Periodo 2 (P2)' },
  { value: 'P3', label: 'Periodo 3 (P3)' },
  { value: 'P4', label: 'Periodo 4 (P4)' },
];

/** Distinto de «Estado» (matrícula): alerta / desempeño en el reporte. */
const ACADEMIC_SITUATION_OPTIONS_BASE = [
  { value: '', label: 'Todas las situaciones' },
  { value: 'al_dia', label: 'Al día' },
  { value: 'en_riesgo', label: 'En riesgo' },
  { value: 'critico_fantasma', label: 'Crítico / Fantasma' },
];

const ACADEMIC_SITUATION_DEBE_RECUPERAR = { value: 'debe_recuperar', label: 'Debe recuperar' };
const ACADEMIC_SITUATION_PERDIENDO_ANO = { value: 'perdiendo_ano', label: 'Perdiendo el año (consolidado)' };

/** Etiquetas cortas de periodos anuales, para tooltips y badges. */
const PERIOD_SHORT_LABELS = {
  primer: 'P1',
  segundo: 'P2',
  tercer: 'P3',
  cuarto: 'P4',
};
const PERIOD_SEQUENCE = ['primer', 'segundo', 'tercer', 'cuarto'];

/** Config visual del estado consolidado (columna CONSOLIDADO). */
const CONSOLIDATED_CONFIG = {
  losing_year: { label: 'Perdiendo', color: 'red', dot: 'red.500', bg: 'red.100', badgeScheme: 'red' },
  on_track: { label: 'Al día', color: 'green', dot: 'green.400', bg: 'green.100', badgeScheme: 'green' },
  no_closed_periods: { label: 'Sin cierre', color: 'gray', dot: 'gray.400', bg: 'gray.100', badgeScheme: 'gray' },
};

const PERIOD_PROGRAM_SLUGS = [
  'primaria-anual-para-ninos',
  'bachillerato-anual-para-adolescentes',
  'bachillerato-flexible',
];

const BRAND_BUTTON = '#574feb';

const DEFAULT_ASUNTO = 'Alerta académica: reporte de rendimiento Klazia';

const DEFAULT_MENSAJE_INICIAL = `Estimado/a acudiente, reciba un cordial saludo.
El presente comunicado tiene como objetivo informarle sobre el estado de rendimiento académico del estudiante, con el fin de realizar un acompañamiento conjunto en su proceso de formación. Es importante tener en cuenta que este reporte corresponde al estado académico actual y no constituye un resultado definitivo, ya que el período aún no ha finalizado y el estudiante todavía puede ponerse al día con actividades, quices, evaluaciones y demás compromisos pendientes.`;

const DEFAULT_MENSAJE_FINAL = `Agradecemos su atención, compromiso y acompañamiento permanente en el proceso académico del estudiante. Para cualquier inquietud o información adicional, la Coordinación de Bienestar Estudiantil se encuentra a su disposición.`;

const DEFAULT_ASUNTO_RECUPERACION = 'Proceso de recuperación académica - {{studentName}}';

const DEFAULT_MENSAJE_INICIAL_RECUPERACION = `Estimado/a acudiente, reciba un cordial saludo.
El presente comunicado tiene como objetivo informarle que el estudiante debe realizar proceso de recuperación académica correspondiente.

Los exámenes de recuperación se encuentran disponibles en la plataforma educativa del estudiante. Agradecemos realizar el acompañamiento necesario para que ingrese, revise las asignaturas pendientes y presente las evaluaciones dentro del plazo establecido.

Una vez finalicen las fechas establecidas para presentar las recuperaciones, la plataforma se cerrará y no se recibirán exámenes por ningún otro medio.`;

const DEFAULT_ASUNTO_RIESGO_ABIERTO = 'Alerta académica: materias en riesgo - {{studentName}}';

const DEFAULT_MENSAJE_INICIAL_RIESGO_ABIERTO = `Estimado/a acudiente, reciba un cordial saludo.
El presente comunicado tiene como objetivo informarle que, a la fecha, el estudiante presenta una o más materias con nota por debajo del mínimo aprobatorio.

El período académico aún se encuentra en curso, por lo que el estudiante todavía puede mejorar su desempeño completando las actividades, quices y evaluaciones pendientes disponibles en la plataforma educativa. Agradecemos realizar el acompañamiento necesario para que ingrese y se ponga al día antes del cierre del período.

En caso de que las materias continúen sin aprobar al cierre del período, deberán presentarse posteriormente en el proceso formal de recuperación académica.`;

const ALERT_CONFIG = {
  on_track: { label: 'Al día', color: 'green', dot: 'green.400' },
  at_risk: { label: 'En Riesgo', color: 'yellow', dot: 'yellow.400' },
  critical: { label: 'Riesgo Crítico', color: 'red', dot: 'red.400' },
  ghost: { label: 'Fantasma', color: 'gray', dot: 'gray.600' },
  must_recover: { label: 'Debe recuperar', color: 'orange', dot: 'orange.400' },
  recovered: { label: 'Recuperó', color: 'teal', dot: 'teal.400' },
};

const LEGEND_KEYS_OPEN = ['on_track', 'at_risk', 'critical', 'ghost'];
const LEGEND_KEYS_CLOSED = ['on_track', 'must_recover', 'recovered', 'ghost'];

function getInitials(student) {
  const first = (student?.firstName || '').trim().charAt(0) || '';
  const last = (student?.lastName || '').trim().charAt(0) || '';
  return (first + last).toUpperCase() || '?';
}

function getFullName(student) {
  return `${student?.firstName || ''} ${student?.lastName || ''}`.trim() || '—';
}

function progressColor(pct) {
  if (pct >= 50) return 'green';
  if (pct >= 20) return 'yellow';
  return 'red';
}

function scoreColor(score, passingThreshold = 6.5) {
  return score >= passingThreshold ? 'green.600' : 'red.500';
}

/**
 * Serializa los periodos incluidos en el consolidado como "P1·P2" para el badge.
 */
function formatClosedPeriodsInline(closedPeriods) {
  if (!Array.isArray(closedPeriods) || !closedPeriods.length) return '';
  return closedPeriods
    .map((k) => PERIOD_SHORT_LABELS[k] || k)
    .join('·');
}

/**
 * Celda de la columna CONSOLIDADO: badge por estado + tooltip con desglose
 * (materia × periodo · promedio). Silenciosa si el estudiante no aplica.
 */
function ConsolidatedCell({ yc }) {
  const status = yc?.status || 'no_closed_periods';
  const cfg = CONSOLIDATED_CONFIG[status] || CONSOLIDATED_CONFIG.no_closed_periods;
  const closed = Array.isArray(yc?.closedPeriods) ? yc.closedPeriods : [];
  const closedLabel = formatClosedPeriodsInline(closed);
  const failedCount = Number(yc?.subjectsFailed || 0);
  const badgeText =
    status === 'losing_year'
      ? `Perdiendo ${failedCount}m`
      : status === 'on_track'
        ? 'Al día'
        : 'Sin cierre';

  const breakdown = Array.isArray(yc?.breakdown) ? yc.breakdown : [];
  const periodsInBreakdown = closed.length
    ? closed
    : PERIOD_SEQUENCE.filter((k) => breakdown.some((b) => b.scoresByPeriod?.[k] != null));

  const tooltipContent =
    status === 'no_closed_periods' ? (
      <Text fontSize="xs">Aún no hay periodos cerrados del año para este estudiante.</Text>
    ) : (
      <Box fontSize="xs">
        <Text fontWeight="bold" mb={1.5}>
          {status === 'losing_year'
            ? `Perdiendo el año · ${failedCount} materia${failedCount === 1 ? '' : 's'} < 6.5`
            : 'Al día en el consolidado'}
        </Text>
        <Box as="table" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <Box as="thead">
            <Box as="tr">
              <Box as="th" textAlign="left" px={1} py={0.5} borderBottomWidth="1px" borderColor="whiteAlpha.400">
                Materia
              </Box>
              {periodsInBreakdown.map((k) => (
                <Box
                  as="th"
                  key={k}
                  textAlign="center"
                  px={1}
                  py={0.5}
                  borderBottomWidth="1px"
                  borderColor="whiteAlpha.400"
                >
                  {PERIOD_SHORT_LABELS[k] || k}
                </Box>
              ))}
              <Box
                as="th"
                textAlign="center"
                px={1}
                py={0.5}
                borderBottomWidth="1px"
                borderColor="whiteAlpha.400"
              >
                Prom.
              </Box>
              <Box as="th" px={1} py={0.5} borderBottomWidth="1px" borderColor="whiteAlpha.400"></Box>
            </Box>
          </Box>
          <Box as="tbody">
            {breakdown.length === 0 ? (
              <Box as="tr">
                <Box as="td" colSpan={periodsInBreakdown.length + 3} px={1} py={0.5}>
                  Sin datos.
                </Box>
              </Box>
            ) : (
              breakdown.map((b) => (
                <Box as="tr" key={b.name}>
                  <Box as="td" px={1} py={0.5}>
                    {b.name}
                  </Box>
                  {periodsInBreakdown.map((k) => (
                    <Box as="td" key={k} textAlign="center" px={1} py={0.5}>
                      {b.scoresByPeriod?.[k] != null ? Number(b.scoresByPeriod[k]).toFixed(1) : '—'}
                    </Box>
                  ))}
                  <Box as="td" textAlign="center" px={1} py={0.5} fontWeight="bold">
                    {Number(b.average).toFixed(2)}
                  </Box>
                  <Box as="td" textAlign="center" px={1} py={0.5}>
                    {b.failed ? '✖' : ''}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>
    );

  return (
    <Tooltip label={tooltipContent} placement="top" hasArrow bg="gray.800" color="white" p={2} maxW="360px">
      <Flex direction="column" align="center" gap={0.5} cursor="help">
        <Badge colorScheme={cfg.badgeScheme} variant="subtle" px={2} py={0.5} borderRadius="full">
          {badgeText}
        </Badge>
        {(closedLabel || (yc?.excludedPeriods || []).length > 0) && (
          <Text fontSize="10px" color="gray.500">
            {closedLabel}
            {(yc?.excludedPeriods || []).length > 0
              ? `${closedLabel ? ' ' : ''}(excl. ${formatClosedPeriodsInline(yc.excludedPeriods)})`
              : ''}
          </Text>
        )}
      </Flex>
    </Tooltip>
  );
}

/**
 * Chip del header del drawer: estado consolidado del año junto al estado del
 * periodo, con un toggle para desplegar el desglose (patrón "progressive
 * disclosure": el resumen siempre visible, el detalle bajo demanda).
 */
function ConsolidatedChip({ yc, isOpen, onToggle }) {
  const status = yc?.status || 'no_closed_periods';
  const cfg = CONSOLIDATED_CONFIG[status] || CONSOLIDATED_CONFIG.no_closed_periods;
  const closed = Array.isArray(yc?.closedPeriods) ? yc.closedPeriods : [];
  const excluded = Array.isArray(yc?.excludedPeriods) ? yc.excludedPeriods : [];
  const failedCount = Number(yc?.subjectsFailed || 0);
  const label =
    status === 'losing_year'
      ? `Año: perdiendo ${failedCount}m`
      : status === 'on_track'
        ? 'Año: al día'
        : 'Año: sin cierre';
  const hasBreakdown = (yc?.breakdown || []).length > 0;
  const tip = [
    'Consolidado del año con los periodos ya cerrados.',
    closed.length ? `Incluye ${formatClosedPeriodsInline(closed)}.` : '',
    excluded.length ? `Excluye ${formatClosedPeriodsInline(excluded)} (cerró antes de la matrícula).` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <HStack spacing={1}>
      <Tooltip label={tip} hasArrow placement="top">
        <Badge colorScheme={cfg.badgeScheme} variant="subtle" fontSize="xs">
          {label}
        </Badge>
      </Tooltip>
      {hasBreakdown && (
        <Button
          size="xs"
          variant="link"
          colorScheme="purple"
          rightIcon={<FiChevronDown style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />}
          onClick={onToggle}
          aria-expanded={isOpen}
        >
          {isOpen ? 'Ocultar desglose' : 'Ver desglose'}
        </Button>
      )}
    </HStack>
  );
}

/**
 * Desglose del consolidado: materia × periodo cerrado × promedio anual.
 */
function ConsolidatedBreakdownTable({ yc, threshold = 6.5 }) {
  const closed = Array.isArray(yc?.closedPeriods) ? yc.closedPeriods : [];
  const breakdown = Array.isArray(yc?.breakdown) ? yc.breakdown : [];
  const failedCount = Number(yc?.subjectsFailed || 0);

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
      <Flex px={3} py={2} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200" justify="space-between" align="center">
        <Text fontSize="xs" fontWeight="semibold" color="gray.700">
          Consolidado del año
        </Text>
        <Text fontSize="xs" color="gray.500">
          {failedCount > 0 ? `${failedCount} con promedio < ${threshold}` : `Todas con promedio ≥ ${threshold}`}
        </Text>
      </Flex>
      {breakdown.length > 0 && (
        <TableContainer maxW="100%" overflowX="auto">
          <Table size="sm" variant="simple" bg="white" sx={{ tableLayout: 'auto' }}>
            <Thead>
              <Tr>
                <Th px={3}>Materia</Th>
                {closed.map((k) => (
                  <Th key={k} textAlign="center" px={2}>
                    {PERIOD_SHORT_LABELS[k] || k}
                  </Th>
                ))}
                <Th textAlign="center" px={2}>
                  <Tooltip label="Promedio anual con los periodos cerrados" hasArrow placement="top">
                    <Text as="span" cursor="help">
                      Prom.
                    </Text>
                  </Tooltip>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {breakdown.map((b) => (
                <Tr key={b.name} bg={b.failed ? 'red.50' : undefined}>
                  <Td fontSize="sm" px={3} whiteSpace="normal" lineHeight="short">
                    {b.name}
                  </Td>
                  {closed.map((k) => (
                    <Td key={k} textAlign="center" fontSize="sm" px={2}>
                      {b.scoresByPeriod?.[k] != null ? Number(b.scoresByPeriod[k]).toFixed(2) : '—'}
                    </Td>
                  ))}
                  <Td textAlign="center" px={2}>
                    <Badge colorScheme={b.failed ? 'red' : 'green'} variant="subtle" px={2} borderRadius="full">
                      {Number(b.average).toFixed(2)}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function formatEngagementDate(iso) {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YY') : '—';
}

export default function ReporteAcademico() {
  const { user } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => ({
    program_type: searchParams.get('program_type') || '',
    program: searchParams.get('program') || '',
    group: searchParams.get('group') || '',
    status: searchParams.get('status') ?? DEFAULT_STATUS_FILTER,
    semester: searchParams.get('semester') || '',
    school: searchParams.get('school') || '',
    academic_situation: searchParams.get('academic_situation') || '',
  }));
  const [period, setPeriod] = useState(() => searchParams.get('period') || '');
  const [report, setReport] = useState({ data: [], meta: null });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isDetailExportModalOpen, setIsDetailExportModalOpen] = useState(false);
  const [detailExportPhase, setDetailExportPhase] = useState('idle');
  const [detailExportCount, setDetailExportCount] = useState(null);
  const [detailExportMax, setDetailExportMax] = useState(80);
  const [isCertExportModalOpen, setIsCertExportModalOpen] = useState(false);
  const [certExportPhase, setCertExportPhase] = useState('idle');
  const [certExportCount, setCertExportCount] = useState(null);
  const [certExportMax, setCertExportMax] = useState(80);
  const [certExportResult, setCertExportResult] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => {
    const n = parseInt(searchParams.get('page'), 10);
    return n >= 1 ? n : 1;
  });

  // Drawer state
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [drawerStudent, setDrawerStudent] = useState(null);
  const [showConsolidated, setShowConsolidated] = useState(false);
  const [drawerResults, setDrawerResults] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const isPeriodProgram = PERIOD_PROGRAM_SLUGS.includes(filters.program_type);
  const isTechnicalCareer = filters.program_type === TECHNICAL_CAREER_SLUG;
  const isSemestralizado = filters.program_type === SEMESTRALIZADO_SLUG;
  const periodOptions = isTechnicalCareer ? PERIOD_OPTIONS_TECHNICAL : PERIOD_OPTIONS;
  const usesSubjectsRealizadas =
    report.meta?.summary?.usesSubjectsRealizadas ?? (isTechnicalCareer || isSemestralizado);
  const reportPassingThreshold = report.meta?.summary?.reportPassingThreshold ?? 6.5;
  const allFiltersFilled =
    Boolean(filters.program_type && filters.program && filters.group && filters.semester) &&
    Boolean(period && period !== '');
  /** Insertar notas: estado obligatorio (además de lo mismo que allFiltersFilled). */
  const allFiltersForInsertNotas = allFiltersFilled && Boolean(filters.status);
  const totalStudents = report.meta?.pagination?.total ?? 0;

  const bulkNotifyTimeScope = useMemo(
    () => `${filters.semester || 'NONE'}_${period || 'NONE'}`,
    [filters.semester, period]
  );
  const canBulkNotify = Boolean(
    filters.program_type &&
    filters.program &&
    filters.status &&
    (filters.semester || filters.group) &&
    totalStudents > 0
  );

  const { isOpen: isBulkNotesOpen, onOpen: onBulkNotesOpen, onClose: onBulkNotesClose } = useDisclosure();
  const [bulkNotesStep, setBulkNotesStep] = useState('idle');
  const [bulkNotesResult, setBulkNotesResult] = useState(null);
  const [bulkNotesError, setBulkNotesError] = useState(null);

  const { isOpen: isReportEmailOpen, onOpen: onReportEmailOpen, onClose: onReportEmailClose } = useDisclosure();
  const [reportEmailDestinatario, setReportEmailDestinatario] = useState('');
  const [reportEmailAsunto, setReportEmailAsunto] = useState('');
  const [reportEmailMensajeInicial, setReportEmailMensajeInicial] = useState(DEFAULT_MENSAJE_INICIAL);
  const [reportEmailMensajeFinal, setReportEmailMensajeFinal] = useState(DEFAULT_MENSAJE_FINAL);
  const [reportEmailPreviewHtml, setReportEmailPreviewHtml] = useState('');
  const [reportEmailLoadingPreview, setReportEmailLoadingPreview] = useState(false);
  const [reportEmailLoadingSend, setReportEmailLoadingSend] = useState(false);
  const reportEmailPreviewDebounceRef = useRef(null);
  const reportEmailPreviewRequestRef = useRef({ mensajeInicial: '', mensajeFinal: '' });
  const reportEmailLatestRef = useRef({ mensajeInicial: '', mensajeFinal: '', asunto: '' });

  const { isOpen: isBulkEmailOpen, onOpen: onBulkEmailOpen, onClose: onBulkEmailClose } = useDisclosure();
  const [bulkEmailStep, setBulkEmailStep] = useState('configuring');
  const [bulkEmailAsunto, setBulkEmailAsunto] = useState('');
  const [bulkEmailMensajeInicial, setBulkEmailMensajeInicial] = useState(DEFAULT_MENSAJE_INICIAL);
  const [bulkEmailMensajeFinal, setBulkEmailMensajeFinal] = useState(DEFAULT_MENSAJE_FINAL);
  const [bulkEmailLoading, setBulkEmailLoading] = useState(false);
  const [bulkEmailResult, setBulkEmailResult] = useState(null);
  const [bulkEmailError, setBulkEmailError] = useState(null);
  const [bulkEmailIncludeAlreadySent, setBulkEmailIncludeAlreadySent] = useState(false);

  /** id -> nombre legible para resumen del modal masivo */
  const [filterLabelMaps, setFilterLabelMaps] = useState({ semesters: {}, groups: {} });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await xhr('/semesters?pagination[limit]=-1&filters[$or][0][school][id][$null]=true&filters[$or][1][school][id][$eq]=0');
        const list = res?.data?.data || [];
        const semesters = {};
        for (const el of list) {
          semesters[String(el.id)] = el.name || `Semestre ${el.id}`;
        }
        if (!cancelled) setFilterLabelMaps((m) => ({ ...m, semesters }));
      } catch {
        if (!cancelled) setFilterLabelMaps((m) => ({ ...m, semesters: {} }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!filters.program_type || !filters.program) {
      setFilterLabelMaps((m) => ({ ...m, groups: {} }));
      return undefined;
    }
    (async () => {
      try {
        const filterKey = filters.program_type === 'carreras-tecnicas' ? 'technical_career' : 'grade';
        const res = await xhr(
          `/class-groups?filters[${filterKey}][id][$eq]=${Number(filters.program)}&filters[active][$eq]=true`
        );
        const list = res?.data?.data || [];
        const groups = {};
        for (const el of list) {
          groups[String(el.id)] = el.name && el.name !== '.' ? el.name : `Grupo ${el.id}`;
        }
        if (!cancelled) setFilterLabelMaps((m) => ({ ...m, groups }));
      } catch {
        if (!cancelled) setFilterLabelMaps((m) => ({ ...m, groups: {} }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters.program_type, filters.program]);

  useEffect(() => {
    reportEmailLatestRef.current = {
      mensajeInicial: reportEmailMensajeInicial,
      mensajeFinal: reportEmailMensajeFinal,
      asunto: reportEmailAsunto,
    };
  }, [reportEmailMensajeInicial, reportEmailMensajeFinal, reportEmailAsunto]);

  const hasMinimumFilters = Boolean(filters.program_type);

  const syncFiltersToUrl = useCallback(
    (nextFilters, nextPeriod, page) => {
      const next = new URLSearchParams();
      if (nextFilters.program_type) next.set('program_type', nextFilters.program_type);
      if (nextFilters.program) next.set('program', nextFilters.program);
      if (nextFilters.group) next.set('group', nextFilters.group);
      if (nextFilters.status) next.set('status', nextFilters.status);
      if (nextFilters.semester) next.set('semester', nextFilters.semester);
      if (nextFilters.school) next.set('school', nextFilters.school);
      if (nextFilters.academic_situation) next.set('academic_situation', nextFilters.academic_situation);
      if (nextPeriod) next.set('period', nextPeriod);
      if (page > 1) next.set('page', String(page));
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const loadReport = useCallback(async () => {
    if (!hasMinimumFilters) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        program_type: filters.program_type,
        page: String(currentPage),
        pageSize: String(PAGE_SIZE),
      });
      if (filters.program) params.set('program', filters.program);
      if (filters.group) params.set('class_group', filters.group);
      if (filters.status) params.set('status', filters.status);
      if (filters.semester) params.set('semester', filters.semester);
      if (filters.school) params.set('school', filters.school);
      if (filters.academic_situation) params.set('academic_situation', filters.academic_situation);
      if (period) params.set('period', period);

      const res = await xhr(`/students/academic-report?${params.toString()}`);
      const body = res.data || res;
      setReport({ data: body.data || [], meta: body.meta || null });
    } catch (err) {
      console.error('Error loading academic report:', err);
      setReport({ data: [], meta: null });
    } finally {
      setLoading(false);
    }
  }, [hasMinimumFilters, filters, period, currentPage]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleFilterChange = useCallback(
    (newFilters) => {
      const prevType = filters.program_type;
      const nextType = newFilters.program_type;
      const wasTechnical = prevType === TECHNICAL_CAREER_SLUG;
      const isTechnical = nextType === TECHNICAL_CAREER_SLUG;
      let nextPeriod = period;
      if (prevType !== nextType && (wasTechnical || isTechnical)) {
        nextPeriod = '';
        setPeriod('');
      }
      setFilters(newFilters);
      setCurrentPage(1);
      syncFiltersToUrl(newFilters, nextPeriod, 1);
    },
    [syncFiltersToUrl, period, filters.program_type]
  );

  const handlePeriodChange = useCallback(
    (e) => {
      const val = e.target.value;
      setPeriod(val);
      setCurrentPage(1);
      syncFiltersToUrl(filters, val, 1);
    },
    [filters, syncFiltersToUrl]
  );

  const handleSchoolChange = useCallback(
    (e) => {
      const val = e.target.value;
      setCurrentPage(1);
      setFilters((prev) => {
        const next = { ...prev, school: val };
        syncFiltersToUrl(next, period, 1);
        return next;
      });
    },
    [period, syncFiltersToUrl]
  );

  const handleAcademicSituationChange = useCallback(
    (e) => {
      const val = e.target.value;
      setCurrentPage(1);
      setFilters((prev) => {
        const next = { ...prev, academic_situation: val };
        syncFiltersToUrl(next, period, 1);
        return next;
      });
    },
    [period, syncFiltersToUrl]
  );

  const handlePageChange = useCallback(
    (event) => {
      const page = event.selected + 1;
      setCurrentPage(page);
      syncFiltersToUrl(filters, period, page);
    },
    [filters, period, syncFiltersToUrl]
  );

  const handleViewDetail = useCallback(
    async (row) => {
      setDrawerStudent(row);
      setDrawerResults(null);
      setShowConsolidated(false);
      onOpen();

      const programInfo = report.meta?.programInfo;
      if (!programInfo?.programTypeSlug || !programInfo?.programSlug) return;

      try {
        setDrawerLoading(true);
        const params = new URLSearchParams();
        if (period) params.set('period', period);
        const query = params.toString();
        const url = `/courses/${programInfo.programTypeSlug}/${programInfo.programSlug}/${row.student.id}/results${query ? `?${query}` : ''}`;
        const res = await xhr(url);
        setDrawerResults(res.data || res);
      } catch (err) {
        console.error('Error loading student detail:', err);
        setDrawerResults([]);
      } finally {
        setDrawerLoading(false);
      }
    },
    [onOpen, report.meta, period]
  );

  const handleDownloadExcel = useCallback(async () => {
    if (!hasMinimumFilters) return;
    setDownloading(true);
    try {
      const params = new URLSearchParams({
        program_type: filters.program_type,
        page: '1',
        pageSize: '500',
      });
      if (filters.program) params.set('program', filters.program);
      if (filters.group) params.set('class_group', filters.group);
      if (filters.status) params.set('status', filters.status);
      if (filters.semester) params.set('semester', filters.semester);
      if (filters.school) params.set('school', filters.school);
      if (filters.academic_situation) params.set('academic_situation', filters.academic_situation);
      if (period) params.set('period', period);

      const res = await xhr(`/students/academic-report?${params.toString()}`);
      const body = res.data || res;
      const allRows = body.data || [];
      const exportUsesRealizadas =
        body.meta?.summary?.usesSubjectsRealizadas ??
        (filters.program_type === TECHNICAL_CAREER_SLUG || filters.program_type === SEMESTRALIZADO_SLUG);
      const exportAppliesConsolidated = Boolean(body.meta?.summary?.appliesConsolidated);

      const header = [
        'ID',
        'NOMBRES',
        'EMAIL',
        'PROGRESO %',
        'PROMEDIO',
        exportUsesRealizadas ? 'MAT. REALIZADAS' : 'MAT. PERDIDAS',
        'TOTAL MAT.',
        'ESTADO',
        ...(exportAppliesConsolidated ? ['CONSOLIDADO', 'MAT. PERDIDAS AÑO', 'PERIODOS INCLUIDOS'] : []),
        'ULT. ACT.',
        'ULT. PAGO',
      ];
      const rows = allRows.map((r) => {
        const yc = r.yearlyConsolidated || {};
        const consolidadoLabel =
          yc.status === 'losing_year'
            ? `Perdiendo (${Number(yc.subjectsFailed || 0)}m)`
            : yc.status === 'on_track'
              ? 'Al día'
              : 'Sin cierre';
        const closedPeriodsLabel = Array.isArray(yc.closedPeriods)
          ? yc.closedPeriods.map((k) => PERIOD_SHORT_LABELS[k] || k).join(', ')
          : '';
        return [
          r.student?.id ?? '—',
          getFullName(r.student),
          r.student?.email ?? '—',
          `${r.generalProgress}%`,
          r.averageScore.toFixed(2),
          exportUsesRealizadas ? r.subjectsPassed ?? 0 : r.subjectsFailed,
          r.totalSubjects,
          ALERT_CONFIG[r.alertStatus]?.label || r.alertStatus,
          ...(exportAppliesConsolidated
            ? [consolidadoLabel, Number(yc.subjectsFailed || 0), closedPeriodsLabel]
            : []),
          formatEngagementDate(r.lastActivityAt),
          formatEngagementDate(r.lastPaymentAt),
        ];
      });

      downloadExcel({
        fileName: `seguimiento-academico-${dayjs().format('YYYY-MM-DD')}`,
        sheet: 'seguimiento-academico',
        tablePayload: { header, body: rows },
      });
    } catch (err) {
      console.error('Error descargando excel:', err);
    } finally {
      setDownloading(false);
    }
  }, [hasMinimumFilters, filters, period]);

  const detailExportModalSummary = useMemo(() => {
    const pi = report.meta?.programInfo;
    const lines = [];
    if (pi?.programTypeName) lines.push(`Tipo de programa: ${pi.programTypeName}`);
    if (pi?.programName) lines.push(`Programa: ${pi.programName}`);
    if (filters.group) {
      const gname = filterLabelMaps.groups[String(filters.group)];
      lines.push(`Grupo: ${gname ? `Grupo ${gname}` : filters.group}`);
    }
    if (filters.status) {
      const st = [ACTIVE_REPORT_STATUS_OPTION, ...STATUS_FILTER_OPTIONS].find((o) => o.value === filters.status)?.label;
      lines.push(`Estado matrícula: ${st || filters.status}`);
    }
    if (filters.semester) {
      const sname = filterLabelMaps.semesters[String(filters.semester)];
      lines.push(`Semestre: ${sname || filters.semester}`);
    }
    if (filters.school) {
      const lab = SCHOOL_FILTER_OPTIONS.find((o) => o.value === filters.school)?.label;
      if (lab) lines.push(`Colegio: ${lab}`);
    }
    return lines;
  }, [report.meta?.programInfo, filters, filterLabelMaps]);

  const handleDownloadDetailedExport = useCallback(async () => {
    if (!filters.program_type || !filters.program) {
      toast({
        title: 'Selecciona tipo y programa',
        description: 'Necesitamos el programa para armar el Excel detallado.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setIsDetailExportModalOpen(true);
    setDetailExportPhase('counting');
    setDetailExportCount(null);
    try {
      const params = new URLSearchParams({
        program_type: filters.program_type,
        program: filters.program,
      });
      if (filters.group) params.set('class_group', filters.group);
      if (filters.semester) params.set('semester', filters.semester);
      if (filters.school) params.set('school', filters.school);
      if (filters.status) params.set('status', filters.status);

      const countRes = await xhr.get(`/students/detailed-academic-export/count?${params.toString()}`);
      const payload = countRes.data;
      const c = payload?.count ?? 0;
      const max = payload?.maxStudents ?? 80;
      setDetailExportCount(c);
      setDetailExportMax(max);

      if (c === 0) {
        toast({
          title: 'Sin estudiantes',
          description: 'No hay estudiantes que coincidan con los filtros actuales (misma lógica que la tabla de arriba).',
          status: 'info',
          duration: 6000,
          isClosable: true,
        });
        setIsDetailExportModalOpen(false);
        setDetailExportPhase('idle');
        return;
      }

      setDetailExportPhase('generating');
      const res = await xhr.post(
        '/students/detailed-academic-export',
        {
          program_type: filters.program_type,
          program: filters.program,
          class_group: filters.group || undefined,
          semester: filters.semester || undefined,
          school: filters.school || undefined,
          status: filters.status || undefined,
          period: period || undefined,
        },
        { responseType: 'blob', timeout: DETAILED_ACADEMIC_EXPORT_TIMEOUT_MS }
      );
      const blob = res.data;
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `reporte-academico-detalle-${filters.program}-${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
      toast({
        title: 'Archivo generado',
        description: 'La descarga debería haber comenzado automáticamente.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setIsDetailExportModalOpen(false);
      setDetailExportPhase('idle');
    } catch (err) {
      console.error('Export detallado:', err);
      let msg = 'No se pudo generar el archivo. Intenta acotar filtros o reintenta.';
      const resData = err.response?.data;
      if (resData instanceof Blob) {
        try {
          const text = await resData.text();
          const j = JSON.parse(text);
          msg = j.error?.message || j.message || msg;
        } catch (e) {
          /* ignore */
        }
      } else if (typeof resData?.error?.message === 'string') {
        msg = resData.error.message;
      } else if (err.message) {
        msg = err.message;
      }
      toast({
        title: 'Error al exportar',
        description: msg,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
      setIsDetailExportModalOpen(false);
      setDetailExportPhase('idle');
    }
  }, [filters, period, toast]);

  const handleDownloadBulkCertificates = useCallback(async () => {
    if (!filters.program_type || !filters.program) {
      toast({
        title: 'Selecciona tipo y programa',
        description: 'Necesitamos el programa para generar los certificados en lote.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (!filters.school) {
      toast({
        title: 'Selecciona un colegio',
        description: 'La descarga masiva de certificados es por colegio: elige el colegio en los filtros.',
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
      return;
    }
    setIsCertExportModalOpen(true);
    setCertExportPhase('counting');
    setCertExportCount(null);
    setCertExportResult(null);
    try {
      const params = new URLSearchParams({
        program_type: filters.program_type,
        program: filters.program,
      });
      if (filters.group) params.set('class_group', filters.group);
      if (filters.semester) params.set('semester', filters.semester);
      params.set('school', filters.school);
      if (filters.status) params.set('status', filters.status);

      const countRes = await xhr.get(`/students/bulk-certificate-export/count?${params.toString()}`);
      const payload = countRes.data;
      const c = payload?.count ?? 0;
      const max = payload?.maxStudents ?? 80;
      setCertExportCount(c);
      setCertExportMax(max);

      if (c === 0) {
        toast({
          title: 'Sin estudiantes',
          description: 'No hay estudiantes que coincidan con los filtros actuales (misma lógica que el reporte).',
          status: 'info',
          duration: 6000,
          isClosable: true,
        });
        setIsCertExportModalOpen(false);
        setCertExportPhase('idle');
        return;
      }

      setCertExportPhase('generating');
      const res = await xhr.post(
        '/students/bulk-certificate-export',
        {
          program_type: filters.program_type,
          program: filters.program,
          class_group: filters.group || undefined,
          semester: filters.semester || undefined,
          school: filters.school,
          status: filters.status || undefined,
          include_report: true,
        },
        { timeout: DETAILED_ACADEMIC_EXPORT_TIMEOUT_MS }
      );
      const result = res.data;
      if (result.pdfBase64) {
        const bytes = Uint8Array.from(window.atob(result.pdfBase64), character => character.charCodeAt(0));
        downloadCertificateFile(new Blob([bytes], { type: 'application/pdf' }), result.filename);
      }
      setCertExportResult(result);
      toast({
        title: result.generated ? 'Certificados generados' : 'No se generaron certificados',
        description: `${result.generated} generados y ${result.skipped.length} omitidos.`,
        status: result.skipped.length ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
      });
      setCertExportPhase('idle');
    } catch (err) {
      console.error('Export certificados lote:', err);
      let msg = 'No se pudo generar el PDF. Intenta acotar filtros, revisa que existan plantillas en el servidor o reintenta.';
      const resData = err.response?.data;
      if (resData instanceof Blob) {
        try {
          const text = await resData.text();
          const j = JSON.parse(text);
          msg = j.error?.message || j.message || msg;
        } catch (e) {
          /* ignore */
        }
      } else if (typeof resData?.error?.message === 'string') {
        msg = resData.error.message;
      } else if (err.message) {
        msg = err.message;
      }
      toast({
        title: 'Error al generar certificados',
        description: msg,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
      setIsCertExportModalOpen(false);
      setCertExportPhase('idle');
    }
  }, [filters, toast]);

  const handleBulkNotesOpen = useCallback(() => {
    setBulkNotesStep('confirming');
    setBulkNotesResult(null);
    setBulkNotesError(null);
    onBulkNotesOpen();
  }, [onBulkNotesOpen]);

  const handleBulkNotesClose = useCallback(() => {
    onBulkNotesClose();
    setBulkNotesStep('idle');
    setBulkNotesResult(null);
    setBulkNotesError(null);
  }, [onBulkNotesClose]);

  const handleBulkNotesConfirm = useCallback(async () => {
    setBulkNotesStep('processing');
    setBulkNotesError(null);
    try {
      const res = await xhr.post('/students/bulk-insert-period-notes', {
        program_type: filters.program_type,
        program: filters.program,
        class_group: filters.group || undefined,
        period,
        semester: filters.semester || undefined,
        status: filters.status,
      });
      const data = res?.data ?? res;
      setBulkNotesResult(data);
      setBulkNotesStep('success');
    } catch (err) {
      console.error('Error insertando notas:', err);
      setBulkNotesError(err?.message || 'Error al insertar notas');
      setBulkNotesStep('error');
    }
  }, [filters, period]);

  const bulkEmailFilterSummary = useMemo(() => {
    const pi = report.meta?.programInfo;
    const parts = [];
    if (pi?.programTypeName) parts.push(pi.programTypeName);
    if (pi?.programName) parts.push(pi.programName);
    if (filters.group) {
      const gname = filterLabelMaps.groups[String(filters.group)];
      parts.push(gname ? `Grupo ${gname}` : `Grupo ${filters.group}`);
    }
    if (filters.semester) {
      const sname = filterLabelMaps.semesters[String(filters.semester)];
      parts.push(sname ? `Semestre: ${sname}` : `Semestre: ${filters.semester}`);
    }
    if (period) {
      const pl = PERIOD_OPTIONS.find((o) => o.value === period)?.label;
      if (pl) parts.push(pl);
    }
    return parts.length ? parts.join(' · ') : 'Filtros actuales de la barra superior';
  }, [report.meta?.programInfo, filters.group, filters.semester, period, filterLabelMaps]);

  const handleBulkEmailOpen = useCallback(() => {
    const isRecuperacion = filters.academic_situation === 'debe_recuperar';
    const isClosed = Boolean(report.meta?.summary?.isPeriodClosed);
    let asunto = DEFAULT_ASUNTO;
    let inicial = DEFAULT_MENSAJE_INICIAL;
    if (isRecuperacion) {
      asunto = isClosed ? DEFAULT_ASUNTO_RECUPERACION : DEFAULT_ASUNTO_RIESGO_ABIERTO;
      inicial = isClosed ? DEFAULT_MENSAJE_INICIAL_RECUPERACION : DEFAULT_MENSAJE_INICIAL_RIESGO_ABIERTO;
    }
    setBulkEmailStep('configuring');
    setBulkEmailAsunto(asunto);
    setBulkEmailMensajeInicial(inicial);
    setBulkEmailMensajeFinal(DEFAULT_MENSAJE_FINAL);
    setBulkEmailIncludeAlreadySent(false);
    setBulkEmailResult(null);
    setBulkEmailError(null);
    onBulkEmailOpen();
  }, [onBulkEmailOpen, filters.academic_situation, report.meta?.summary?.isPeriodClosed]);

  const handleBulkEmailClose = useCallback(() => {
    onBulkEmailClose();
    setBulkEmailStep('configuring');
    setBulkEmailLoading(false);
    setBulkEmailResult(null);
    setBulkEmailError(null);
  }, [onBulkEmailClose]);

  const handleBulkEmailSend = useCallback(async () => {
    if (!bulkEmailAsunto?.trim() || !filters.program_type || !filters.program) return;
    if (!filters.status) {
      toast({
        title: 'Selecciona estado de matrícula',
        description: 'El envío masivo de correos requiere un estado concreto (no «Todos los estados»).',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setBulkEmailStep('sending');
    setBulkEmailLoading(true);
    setBulkEmailError(null);
    try {
      const res = await xhr.post('/students/bulk-send-academic-report-email', {
        program_type: filters.program_type,
        program: filters.program,
        class_group: filters.group || undefined,
        semester: filters.semester || undefined,
        period: period || undefined,
        status: filters.status,
        timeScope: bulkNotifyTimeScope,
        asunto: bulkEmailAsunto.trim(),
        mensajeInicial: bulkEmailMensajeInicial,
        mensajeFinal: bulkEmailMensajeFinal,
        includeAlreadySent: bulkEmailIncludeAlreadySent,
        academic_situation: filters.academic_situation || undefined,
      });
      const data = res?.data ?? res;
      setBulkEmailResult(data);
      setBulkEmailStep('success');
      const toastParts = [`${data.sent ?? 0} enviados`];
      if (data.alreadySent > 0) toastParts.push(`${data.alreadySent} omitidos (ya enviados)`);
      toast({
        title: 'Envío masivo completado',
        description: toastParts.join(' · '),
        status: 'success',
        duration: 5000,
      });
      loadReport();
    } catch (err) {
      console.error('Error envío masivo reporte:', err);
      setBulkEmailError(err?.message || 'Error al enviar los correos');
      setBulkEmailStep('error');
      toast({ title: 'Error en envío masivo', status: 'error', duration: 5000 });
    } finally {
      setBulkEmailLoading(false);
    }
  }, [
    bulkEmailAsunto,
    bulkEmailMensajeInicial,
    bulkEmailMensajeFinal,
    bulkEmailIncludeAlreadySent,
    bulkNotifyTimeScope,
    filters,
    period,
    loadReport,
    toast,
  ]);

  const getReportSummaryFromDrawer = useCallback(() => {
    if (!drawerStudent) return {};
    const periodLabel =
      period === 'tercer'
        ? 'Tercer periodo'
        : period === 'primer'
          ? 'Primer periodo'
          : period === 'segundo'
            ? 'Segundo periodo'
            : period === 'cuarto'
              ? 'Cuarto periodo'
              : period || '—';
    return {
      studentName: getFullName(drawerStudent.student),
      period: periodLabel,
      alertStatus: drawerStudent.alertStatus,
      generalProgress: drawerStudent.generalProgress,
      averageScore: drawerStudent.averageScore,
      totalSubjects: drawerStudent.totalSubjects ?? (drawerResults?.filter((r) => !r.no_enrollment)?.length || 0),
      isPeriodClosed: Boolean(report.meta?.summary?.isPeriodClosed),
    };
  }, [drawerStudent, drawerResults, period, report.meta?.summary?.isPeriodClosed]);

  const getSubjectResultsForEmail = useCallback(() => {
    const list = drawerResults ? drawerResults.filter((r) => !r.no_enrollment) : [];
    const closed = Boolean(report.meta?.summary?.isPeriodClosed);
    return list.map((r) => {
      const score = Number(r.final_score?.score ?? 0);
      const note = r.recuperationQuizEnrollment?.note;
      const hasSubjectActivity =
        Number(r.activities?.completed ?? 0) > 0 ||
        Number(r.quizzes?.completed ?? 0) > 0 ||
        r.exam?.completed === true;
      let recovery = r.recovery;
      if (note != null && note !== '') {
        const s = Number(note) / 10;
        recovery = {
          score: Number(s.toFixed(2)),
          status: s >= PASSING_SCORE ? 'passed' : 'failed',
        };
      } else if (closed && hasSubjectActivity && score < PASSING_SCORE) {
        recovery = { score: null, status: 'pending' };
      }
      return recovery ? { ...r, recovery } : r;
    });
  }, [drawerResults, report.meta?.summary?.isPeriodClosed]);

  const fetchReportEmailPreview = useCallback(async () => {
    if (!drawerStudent?.student?.id || !report.meta?.programInfo) return;
    const programInfo = report.meta.programInfo;
    const latest = reportEmailLatestRef.current;
    const snapshot = {
      mensajeInicial: latest.mensajeInicial,
      mensajeFinal: latest.mensajeFinal,
    };
    reportEmailPreviewRequestRef.current = snapshot;
    setReportEmailLoadingPreview(true);

    try {
      const res = await xhr.post('/students/preview-academic-report-email', {
        studentId: drawerStudent.student.id,
        programTypeSlug: programInfo.programTypeSlug,
        programSlug: programInfo.programSlug,
        asunto: latest.asunto,
        mensajeInicial: latest.mensajeInicial,
        mensajeFinal: latest.mensajeFinal,
        reportSummary: getReportSummaryFromDrawer(),
        subjectResults: getSubjectResultsForEmail(),
      });
      const data = res?.data ?? res;
      const current = reportEmailLatestRef.current;
      if (current.mensajeInicial === snapshot.mensajeInicial && current.mensajeFinal === snapshot.mensajeFinal) {
        setReportEmailPreviewHtml(data.html || '');
      }
      if (data.parentEmail != null) {
        setReportEmailDestinatario(data.parentEmail);
      }
    } catch (err) {
      console.error('Error generando vista previa:', err);
      const current = reportEmailLatestRef.current;
      if (current.mensajeInicial === snapshot.mensajeInicial && current.mensajeFinal === snapshot.mensajeFinal) {
        setReportEmailPreviewHtml('');
      }
    } finally {
      setReportEmailLoadingPreview(false);
    }
  }, [
    drawerStudent,
    drawerResults,
    report.meta?.programInfo,
    getReportSummaryFromDrawer,
    getSubjectResultsForEmail,
  ]);

  const handleOpenReportEmail = useCallback(() => {
    if (!drawerStudent) return;
    setReportEmailAsunto(`Reporte de Seguimiento Académico - ${getFullName(drawerStudent.student)}`);
    setReportEmailMensajeInicial(DEFAULT_MENSAJE_INICIAL);
    setReportEmailMensajeFinal(DEFAULT_MENSAJE_FINAL);
    setReportEmailPreviewHtml('');
    setReportEmailDestinatario('');
    onReportEmailOpen();
  }, [drawerStudent, onReportEmailOpen]);

  const handleCloseReportEmail = useCallback(() => {
    onReportEmailClose();
    if (reportEmailPreviewDebounceRef.current) clearTimeout(reportEmailPreviewDebounceRef.current);
  }, [onReportEmailClose]);

  const scheduleReportEmailPreview = useCallback(() => {
    if (reportEmailPreviewDebounceRef.current) clearTimeout(reportEmailPreviewDebounceRef.current);
    reportEmailPreviewDebounceRef.current = setTimeout(() => {
      fetchReportEmailPreview();
    }, 400);
  }, [fetchReportEmailPreview]);

  const handleSendReportEmail = useCallback(async () => {
    if (!drawerStudent?.student?.id || !report.meta?.programInfo) return;
    if (!reportEmailAsunto?.trim()) return;
    const programInfo = report.meta.programInfo;
    setReportEmailLoadingSend(true);
    try {
      await xhr.post('/students/send-academic-report-email', {
        studentId: drawerStudent.student.id,
        programTypeSlug: programInfo.programTypeSlug,
        programSlug: programInfo.programSlug,
        asunto: reportEmailAsunto.trim(),
        destinatario: reportEmailDestinatario?.trim() || undefined,
        mensajeInicial: reportEmailMensajeInicial,
        mensajeFinal: reportEmailMensajeFinal,
        reportSummary: getReportSummaryFromDrawer(),
        subjectResults: getSubjectResultsForEmail(),
        timeScope: bulkNotifyTimeScope,
        enrollmentId:
          drawerStudent.enrollmentId != null && drawerStudent.enrollmentId !== ''
            ? Number(drawerStudent.enrollmentId)
            : undefined,
        program_type: filters.program_type || programInfo.programTypeSlug || undefined,
        programTypeSlug: programInfo.programTypeSlug,
        program: filters.program || undefined,
        semester: filters.semester || undefined,
        class_group: filters.group || undefined,
      });
      toast({ title: 'Correo enviado correctamente', status: 'success', duration: 3000 });
      handleCloseReportEmail();
      loadReport();
    } catch (err) {
      console.error('Error enviando correo:', err);
      toast({ title: 'Error al enviar el correo', status: 'error', duration: 4000 });
    } finally {
      setReportEmailLoadingSend(false);
    }
  }, [
    drawerStudent,
    drawerResults,
    report.meta?.programInfo,
    reportEmailAsunto,
    reportEmailDestinatario,
    reportEmailMensajeInicial,
    reportEmailMensajeFinal,
    getReportSummaryFromDrawer,
    getSubjectResultsForEmail,
    handleCloseReportEmail,
    toast,
    bulkNotifyTimeScope,
    filters,
    loadReport,
  ]);

  useEffect(() => {
    if (isReportEmailOpen && drawerStudent?.student?.id && report.meta?.programInfo) {
      fetchReportEmailPreview();
    }
  }, [isReportEmailOpen]);

  const canAccess = ['supervisor_admin', 'admin'].includes(user?.role?.type);
  if (!canAccess) {
    return (
      <Box p={8} textAlign="center">
        <Text color="red.500" fontSize="lg">
          No tienes permisos para acceder a esta sección.
        </Text>
      </Box>
    );
  }

  const pagination = report.meta?.pagination || {};
  const summary = report.meta?.summary || {};
  const periodClosedView = Boolean(summary.isPeriodClosed);
  const appliesConsolidated = Boolean(summary.appliesConsolidated);
  const situationOptions = [
    ...ACADEMIC_SITUATION_OPTIONS_BASE,
    ACADEMIC_SITUATION_DEBE_RECUPERAR,
    ...(appliesConsolidated ? [ACADEMIC_SITUATION_PERDIENDO_ANO] : []),
  ];
  const total = pagination.total || 0;
  const pageCount = pagination.pageCount || 0;
  const start = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const end = Math.min(currentPage * PAGE_SIZE, total);

  return (
    <Box p={4} maxW="100%">
      <SmartFilterBar
        title="Seguimiento Académico"
        description="Selecciona los filtros para ver el rendimiento académico de los estudiantes."
        value={filters}
        onChange={handleFilterChange}
        loading={loading}
        visibleFilters={VISIBLE_REPORT_FILTERS}
        includeActiveStatus
      />

      {hasMinimumFilters && (
        <Flex mt={4} align="center" gap={{ base: 3, md: 8 }} flexWrap="wrap" rowGap={3}>
          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
              Periodo:
            </Text>
            <Select
              size="sm"
              maxW="220px"
              minW="160px"
              bg="white"
              borderColor="gray.200"
              borderRadius="lg"
              fontSize="sm"
              _hover={{ bg: 'gray.50' }}
              value={period}
              onChange={handlePeriodChange}
            >
              {periodOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </Flex>
          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
              Colegio:
            </Text>
            <Select
              size="sm"
              maxW="280px"
              minW="200px"
              bg="white"
              borderColor="gray.200"
              borderRadius="lg"
              fontSize="sm"
              _hover={{ bg: 'gray.50' }}
              value={filters.school || ''}
              onChange={handleSchoolChange}
            >
              {SCHOOL_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value || '__all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </Flex>
          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
              Situación académica:
            </Text>
            <Select
              size="sm"
              maxW="240px"
              minW="180px"
              bg="white"
              borderColor="gray.200"
              borderRadius="lg"
              fontSize="sm"
              _hover={{ bg: 'gray.50' }}
              value={filters.academic_situation || ''}
              onChange={handleAcademicSituationChange}
            >
              {situationOptions.map((opt) => (
                <option key={opt.value || '__all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </Flex>
        </Flex>
      )}

      {!hasMinimumFilters && (
        <Box
          mt={6}
          p={8}
          borderRadius="xl"
          borderWidth="2px"
          borderColor="gray.200"
          borderStyle="dashed"
          bg="gray.50"
          minH="280px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Flex flexDirection="column" align="center" gap={4} maxW="360px">
            <Flex
              w={16}
              h={16}
              borderRadius="full"
              bg="#E8E4FC"
              color="#574feb"
              borderWidth="1px"
              borderColor="#D4CCF0"
              align="center"
              justify="center"
            >
              <Box as={FiFilter} boxSize={8} />
            </Flex>
            <Box textAlign="center">
              <Text fontWeight="semibold" fontSize="lg" color="gray.800" mb={2}>
                Selecciona filtros para ver el reporte
              </Text>
              <Text color="gray.600" fontSize="sm" lineHeight="tall">
                Elige al menos un <strong>Tipo de programa</strong> en la barra de filtros de arriba para generar el
                seguimiento académico.
              </Text>
            </Box>
          </Flex>
        </Box>
      )}

      {hasMinimumFilters && (
        <>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} my={6}>
            <Tooltip label="Cantidad total de estudiantes que coinciden con los filtros." placement="top" hasArrow>
              <Box
                bg="white"
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderWidth="1px"
                borderColor="gray.100"
                cursor="help"
              >
                <Flex align="center" gap={3}>
                  <Box bg="blue.100" p={2} borderRadius="full">
                    <Box as={FiUsers} color="blue.600" boxSize={5} />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      Total Estudiantes
                    </Text>
                    <Text fontWeight="bold" fontSize="lg">
                      {summary.totalStudents ?? 0}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </Tooltip>
            {!periodClosedView ? (
              <>
                <Tooltip
                  label={`Estudiantes con progreso >= 50% y promedio >= ${reportPassingThreshold}`}
                  placement="top"
                  hasArrow
                >
                  <Box
                    bg="white"
                    p={4}
                    borderRadius="lg"
                    boxShadow="sm"
                    borderWidth="1px"
                    borderColor="gray.100"
                    cursor="help"
                  >
                    <Flex align="center" gap={3}>
                      <Box bg="green.100" p={2} borderRadius="full">
                        <Box as={IoIosCheckmarkCircleOutline} color="green.600" boxSize={6} />
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.600">
                          Al Día
                        </Text>
                        <Text fontWeight="bold" fontSize="lg">
                          {summary.onTrack ?? 0}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Tooltip>
                <Tooltip label="Estudiantes con progreso o promedio en zona de riesgo." placement="top" hasArrow>
                  <Box
                    bg="white"
                    p={4}
                    borderRadius="lg"
                    boxShadow="sm"
                    borderWidth="1px"
                    borderColor="gray.100"
                    cursor="help"
                  >
                    <Flex align="center" gap={3}>
                      <Box bg="yellow.100" p={2} borderRadius="full">
                        <Box as={FiAlertTriangle} color="yellow.600" boxSize={5} />
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.600">
                          En Riesgo
                        </Text>
                        <Text fontWeight="bold" fontSize="lg">
                          {summary.atRisk ?? 0}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Tooltip>
                <Tooltip
                  label="Estudiantes con riesgo crítico (bajo rendimiento) o fantasma (sin actividad)."
                  placement="top"
                  hasArrow
                >
                  <Box
                    bg="white"
                    p={4}
                    borderRadius="lg"
                    boxShadow="sm"
                    borderWidth="1px"
                    borderColor="gray.100"
                    cursor="help"
                  >
                    <Flex align="center" gap={3}>
                      <Box bg="red.100" p={2} borderRadius="full">
                        <Box as={FiAlertTriangle} color="red.600" boxSize={5} />
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.600">
                          Crítico / Fantasma
                        </Text>
                        <Text fontWeight="bold" fontSize="lg">
                          {(summary.critical ?? 0) + (summary.ghost ?? 0)}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip
                  label="Periodo cerrado: estudiantes al día (sin pendiente de recuperación)."
                  placement="top"
                  hasArrow
                >
                  <Box
                    bg="white"
                    p={4}
                    borderRadius="lg"
                    boxShadow="sm"
                    borderWidth="1px"
                    borderColor="gray.100"
                    cursor="help"
                  >
                    <Flex align="center" gap={3}>
                      <Box bg="green.100" p={2} borderRadius="full">
                        <Box as={IoIosCheckmarkCircleOutline} color="green.600" boxSize={6} />
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.600">
                          Aprobados
                        </Text>
                        <Text fontWeight="bold" fontSize="lg">
                          {summary.onTrack ?? 0}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Tooltip>
                <Tooltip
                  label="Periodo cerrado: deben presentar o aprobar recuperación en al menos una materia."
                  placement="top"
                  hasArrow
                >
                  <Box
                    bg="white"
                    p={4}
                    borderRadius="lg"
                    boxShadow="sm"
                    borderWidth="1px"
                    borderColor="gray.100"
                    cursor="help"
                  >
                    <Flex align="center" gap={3}>
                      <Box bg="orange.100" p={2} borderRadius="full">
                        <Box as={FiAlertTriangle} color="orange.600" boxSize={5} />
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.600">
                          Debe Recuperar
                        </Text>
                        <Text fontWeight="bold" fontSize="lg">
                          {summary.mustRecover ?? 0}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Tooltip>
                <Tooltip
                  label="Periodo cerrado: aprobaron recuperación en materias que no habían pasado."
                  placement="top"
                  hasArrow
                >
                  <Box
                    bg="white"
                    p={4}
                    borderRadius="lg"
                    boxShadow="sm"
                    borderWidth="1px"
                    borderColor="gray.100"
                    cursor="help"
                  >
                    <Flex align="center" gap={3}>
                      <Box bg="teal.100" p={2} borderRadius="full">
                        <Box as={BiCheckDouble} color="teal.600" boxSize={5} />
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.600">
                          Recuperó
                        </Text>
                        <Text fontWeight="bold" fontSize="lg">
                          {summary.recovered ?? 0}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Tooltip>
              </>
            )}
          </SimpleGrid>

          <Flex align="center" justify="space-between" gap={3} mb={3} flexWrap="wrap">
            <HStack spacing={4} fontSize="sm" color="gray.600" flexWrap="wrap">
              {(periodClosedView ? LEGEND_KEYS_CLOSED : LEGEND_KEYS_OPEN).map((key) => {
                const cfg = ALERT_CONFIG[key];
                if (!cfg) return null;
                return (
                  <HStack spacing={2} key={key}>
                    <Box w={3} h={3} borderRadius="full" bg={cfg.dot} />
                    <Text>{cfg.label}</Text>
                  </HStack>
                );
              })}
            </HStack>
            <HStack spacing={2}>
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="outline"
                  borderColor={BRAND_BUTTON}
                  color={BRAND_BUTTON}
                  _hover={{ bg: BRAND_BUTTON, color: 'white', borderColor: BRAND_BUTTON }}
                  rightIcon={<FiChevronDown />}
                  isDisabled={loading}
                >
                  Acciones
                </MenuButton>
                <MenuList
                  zIndex={20}
                  fontSize="sm"
                  minW="280px"
                  py={1}
                  display="flex"
                  flexDirection="column"
                  alignItems="stretch"
                >
                  {isPeriodProgram && (
                    <MenuItem
                      icon={<FiEdit3 size={18} strokeWidth={2} />}
                      onClick={handleBulkNotesOpen}
                      isDisabled={!allFiltersForInsertNotas}
                      title={
                        !allFiltersForInsertNotas
                          ? 'Completa tipo, programa, grupo, semestre, estado de matrícula y periodo para insertar notas.'
                          : undefined
                      }
                      _hover={{ bg: 'purple.50' }}
                    >
                      Insertar notas finales del periodo
                    </MenuItem>
                  )}
                  <MenuItem
                    icon={<FiSend size={18} strokeWidth={2} />}
                    onClick={handleBulkEmailOpen}
                    isDisabled={!canBulkNotify}
                    title={
                      !canBulkNotify
                        ? 'Selecciona tipo, programa, estado de matrícula y semestre o grupo; debe haber estudiantes (no uses «Todos los estados» en el envío).'
                        : undefined
                    }
                    _hover={{ bg: 'purple.50' }}
                  >
                    Enviar correo masivo
                  </MenuItem>
                </MenuList>
              </Menu>
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  colorScheme="green"
                  isLoading={downloading || detailExportPhase !== 'idle' || certExportPhase !== 'idle'}
                  loadingText="Exportando…"
                  isDisabled={
                    loading ||
                    detailExportPhase === 'counting' ||
                    detailExportPhase === 'generating' ||
                    certExportPhase === 'counting' ||
                    certExportPhase === 'generating'
                  }
                  rightIcon={<FiChevronDown />}
                >
                  Descargar
                </MenuButton>
                <MenuList zIndex={20} fontSize="sm" minW="300px" py={1}>
                  <MenuItem
                    onClick={handleDownloadExcel}
                    isDisabled={!report.data.length}
                    _hover={{ bg: 'green.50' }}
                  >
                    <Box>
                      <Text fontWeight="semibold">Resumen (tabla actual)</Text>
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        Una fila por estudiante con progreso y promedio según el periodo seleccionado arriba.
                      </Text>
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={handleDownloadDetailedExport} _hover={{ bg: 'green.50' }}>
                    <Box>
                      <Text fontWeight="semibold">Detallado por estudiante (Excel)</Text>
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        Mismos filtros que la tabla (estado, grupo, semestre, colegio). Incluye desglose por periodo y
                        materia (actividades, quizzes, examen). Puede tardar varios minutos.
                      </Text>
                    </Box>
                  </MenuItem>
                  <MenuItem
                    onClick={handleDownloadBulkCertificates}
                    isDisabled={!filters.program || !filters.school}
                    _hover={{ bg: 'green.50' }}
                    title={
                      !filters.program
                        ? 'Elige un programa en los filtros para activar esta descarga.'
                        : !filters.school
                          ? 'Elige también un colegio: la descarga es por colegio y acota la carga del servidor.'
                          : undefined
                    }
                  >
                    <Box>
                      <Text fontWeight="semibold">Certificados finales (PDF único)</Text>
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        Un PDF por colegio y programa (mismo certificado que en &quot;Ver notas finales&quot;). Requiere{' '}
                        <strong>programa y colegio</strong> en los filtros; puede tardar varios minutos.
                      </Text>
                    </Box>
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </Flex>

          <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden" borderWidth="1px" borderColor="gray.100">
            <Box bg="#574feb" px={5} py={3}>
              <Flex align="center" gap={3}>
                <Box as={BiGridAlt} color="white" boxSize={6} />
                <Text fontWeight="bold" color="white" fontSize="lg">
                  Seguimiento Académico
                </Text>
              </Flex>
            </Box>

            {loading ? (
              <Flex justify="center" py={12}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <TableContainer overflowX="auto" maxW="100%">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th minW="220px" py={3} px={4} bg="gray.50">
                        ESTUDIANTE
                      </Th>
                      <Th minW="160px" textAlign="center" bg="gray.50" py={3}>
                        PROGRESO GENERAL
                      </Th>
                      <Th minW="100px" textAlign="center" bg="gray.50" py={3}>
                        PROMEDIO
                      </Th>
                      <Th minW="120px" textAlign="center" bg="gray.50" py={3}>
                        <Tooltip
                          label={
                            usesSubjectsRealizadas
                              ? 'Materias con nota ≥ 6.0 en el periodo filtrado'
                              : 'Materias con actividad y nota por debajo del umbral de aprobación'
                          }
                          hasArrow
                          placement="top"
                        >
                          <Text as="span" cursor="help">
                            {usesSubjectsRealizadas ? 'MAT. REALIZ.' : 'MAT. PERDIDAS'}
                          </Text>
                        </Tooltip>
                      </Th>
                      <Th minW="130px" textAlign="center" bg="gray.50" py={3}>
                        ESTADO
                      </Th>
                      {appliesConsolidated && (
                        <Th minW="170px" textAlign="center" bg="gray.50" py={3}>
                          <Tooltip
                            label={`Estado acumulado del año con los periodos ya cerrados. Perdiendo el año = 1 o más asignaturas con promedio < ${reportPassingThreshold}.`}
                            hasArrow
                            placement="top"
                          >
                            <Text as="span" cursor="help">
                              CONSOLIDADO
                            </Text>
                          </Tooltip>
                        </Th>
                      )}
                      <Th minW="88px" textAlign="center" bg="gray.50" py={3} px={2}>
                        <Tooltip label="Última actividad enviada (materias del programa)" hasArrow placement="top">
                          <Text as="span" cursor="help">
                            Últ. act.
                          </Text>
                        </Tooltip>
                      </Th>
                      <Th minW="88px" textAlign="center" bg="gray.50" py={3} px={2}>
                        <Tooltip label="Último pago aprobado/facturado del programa filtrado" hasArrow placement="top">
                          <Text as="span" cursor="help">
                            Últ. pago
                          </Text>
                        </Tooltip>
                      </Th>
                      <Th minW="100px" textAlign="center" bg="gray.50" py={3}>
                        ACCIÓN
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {report.data.map((row) => {
                      const alert = ALERT_CONFIG[row.alertStatus] || ALERT_CONFIG.ghost;
                      const history = row.reportsHistory || row.reports_history;
                      const sentAt = history?.[bulkNotifyTimeScope];
                      return (
                        <Tr key={row.student?.id} _hover={{ bg: 'gray.50' }}>
                          <Td>
                            <Flex align="center" gap={3}>
                              <Flex
                                w={10}
                                h={10}
                                borderRadius="full"
                                bg="#E8E4FC"
                                color="#574feb"
                                borderWidth="1px"
                                borderColor="#D4CCF0"
                                align="center"
                                justify="center"
                                fontSize="xs"
                                fontWeight="bold"
                                flexShrink={0}
                              >
                                {getInitials(row.student)}
                              </Flex>
                              <Box>
                                <Flex align="center" gap={1.5}>
                                  <Link
                                    href={`/plataforma/administrador/ver-estudiante/${row.student?.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    fontWeight="medium"
                                    color="blue.600"
                                    _hover={{ textDecoration: 'underline' }}
                                    fontSize="sm"
                                  >
                                    {getFullName(row.student)}
                                  </Link>

                                  <SchoolBadge student={row.student} ml={0} />

                                  {row.student?.low_performance && (
                                    <Tooltip
                                      label="Bajo rendimiento en los anteriores periodos"
                                      hasArrow
                                      placement="top"
                                    >
                                      <Box as="span" display="inline-flex" alignItems="center">
                                        <Box
                                          as={FiAlertTriangle}
                                          color="red.500"
                                          boxSize={4}
                                          flexShrink={0}
                                        />
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Flex>
                                <Text fontSize="xs" color="gray.500">
                                  ID: {row.student?.id ?? '—'}
                                </Text>
                                {sentAt && (
                                  <Flex align="center" gap={1} mt={0.5} color="green.600">
                                    <MdMarkEmailRead size={14} aria-hidden />
                                    <Text fontSize="xs" color="inherit">
                                      Enviado: {dayjs(sentAt).format('DD/MMM hh:mm A')}
                                    </Text>
                                  </Flex>
                                )}
                              </Box>
                            </Flex>
                          </Td>
                          <Td>
                            <Flex align="center" gap={2} justify="center">
                              <Progress
                                value={row.generalProgress}
                                size="sm"
                                colorScheme={progressColor(row.generalProgress)}
                                borderRadius="full"
                                flex={1}
                                maxW="120px"
                              />
                              <Text fontSize="xs" fontWeight="medium" color="gray.700" minW="36px" textAlign="right">
                                {row.generalProgress}%
                              </Text>
                            </Flex>
                          </Td>
                          <Td textAlign="center">
                            <Text fontWeight="bold" fontSize="sm" color={scoreColor(row.averageScore, reportPassingThreshold)}>
                              {row.averageScore.toFixed(2)}
                            </Text>
                          </Td>
                          <Td textAlign="center">
                            {row.alertStatus === 'ghost' ? (
                              <Text fontSize="sm" color="gray.400">
                                —
                              </Text>
                            ) : (
                              <Text fontSize="sm" fontWeight="medium">
                                {usesSubjectsRealizadas
                                  ? `${row.subjectsPassed ?? 0}/${row.totalSubjects}`
                                  : `${row.subjectsFailed}/${row.totalSubjects}`}
                              </Text>
                            )}
                          </Td>
                          <Td textAlign="center">
                            <Badge colorScheme={alert.color} variant="subtle" px={2} py={0.5} borderRadius="full">
                              {alert.label}
                            </Badge>
                          </Td>
                          {appliesConsolidated && (
                            <Td textAlign="center">
                              <ConsolidatedCell yc={row.yearlyConsolidated} />
                            </Td>
                          )}
                          <Td textAlign="center" px={2}>
                            <Text fontSize="xs" color="gray.700" whiteSpace="nowrap">
                              {formatEngagementDate(row.lastActivityAt)}
                            </Text>
                          </Td>
                          <Td textAlign="center" px={2}>
                            <Text fontSize="xs" color="gray.700" whiteSpace="nowrap">
                              {formatEngagementDate(row.lastPaymentAt)}
                            </Text>
                          </Td>
                          <Td textAlign="center">
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme="purple"
                              leftIcon={<FiEye />}
                              onClick={() => handleViewDetail(row)}
                            >
                              Ver Detalle
                            </Button>
                          </Td>
                        </Tr>
                      );
                    })}
                    {!report.data.length && (
                      <Tr>
                        <Td colSpan={appliesConsolidated ? 9 : 8} textAlign="center" py={8}>
                          <Text color="gray.500">No se encontraron estudiantes con los filtros seleccionados.</Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            )}

            <Flex
              justify="space-between"
              align="center"
              p={4}
              borderTopWidth="1px"
              borderColor="gray.100"
              flexWrap="wrap"
              gap={3}
            >
              <Text color="gray.600" fontSize="sm">
                Mostrando {start}-{end} de {total} estudiantes
              </Text>
              {pageCount > 1 && (
                <ReactPaginate
                  breakLabel="..."
                  nextLabel=">"
                  previousLabel="<"
                  onPageChange={handlePageChange}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  pageCount={pageCount}
                  forcePage={currentPage - 1}
                  renderOnZeroPageCount={null}
                  containerClassName="academicReportPagination"
                  pageClassName="academicReportPaginationItem"
                  activeClassName="academicReportPaginationActive"
                  previousClassName="academicReportPaginationItem"
                  nextClassName="academicReportPaginationItem"
                  disabledClassName="academicReportPaginationDisabled"
                />
              )}
            </Flex>
          </Box>

          <style>{`
            .academicReportPagination {
              display: flex;
              list-style: none;
              gap: 4px;
              margin: 0;
              padding: 0;
            }
            .academicReportPaginationItem {
              display: flex;
              align-items: center;
              justify-content: center;
              min-width: 36px;
              height: 36px;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              cursor: pointer;
              font-size: 14px;
              color: #4a5568;
              transition: all 0.2s;
            }
            .academicReportPaginationItem:hover {
              background-color: #f7fafc;
              border-color: #cbd5e0;
            }
            .academicReportPaginationItem a {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 100%;
              height: 100%;
              padding: 0 12px;
            }
            .academicReportPaginationActive {
              background-color: #574feb !important;
              border-color: #574feb !important;
              color: white !important;
            }
            .academicReportPaginationActive a {
              color: white !important;
            }
            .academicReportPaginationDisabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          `}</style>
        </>
      )}

      {/* Detail Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Flex direction="column" gap={3}>
              <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
                <Flex align="center" gap={3}>
                  <Flex
                    w={10}
                    h={10}
                    borderRadius="full"
                    bg="#E8E4FC"
                    color="#574feb"
                    borderWidth="1px"
                    borderColor="#D4CCF0"
                    align="center"
                    justify="center"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    {getInitials(drawerStudent?.student)}
                  </Flex>
                  <Box>
                    <Text fontSize="md" fontWeight="bold">
                      {getFullName(drawerStudent?.student)}
                    </Text>
                    <Flex gap={3} align="center" wrap="wrap">
                      {drawerStudent && (
                        <Tooltip label="Estado del periodo filtrado" hasArrow placement="top">
                          <Badge
                            colorScheme={ALERT_CONFIG[drawerStudent.alertStatus]?.color}
                            variant="subtle"
                            fontSize="xs"
                          >
                            {ALERT_CONFIG[drawerStudent.alertStatus]?.label}
                          </Badge>
                        </Tooltip>
                      )}
                      {appliesConsolidated && drawerStudent?.yearlyConsolidated?.applies && (
                        <ConsolidatedChip
                          yc={drawerStudent.yearlyConsolidated}
                          isOpen={showConsolidated}
                          onToggle={() => setShowConsolidated((v) => !v)}
                        />
                      )}
                      <Text fontSize="xs" color="gray.500">
                        Promedio: {drawerStudent?.averageScore?.toFixed(2)} | Progreso: {drawerStudent?.generalProgress}%
                      </Text>
                    </Flex>
                  </Box>
                </Flex>
              </Flex>
              {appliesConsolidated && drawerStudent?.yearlyConsolidated?.applies && (
                <Collapse in={showConsolidated} animateOpacity>
                  <Box minW={0} maxW="100%">
                    <ConsolidatedBreakdownTable yc={drawerStudent.yearlyConsolidated} threshold={reportPassingThreshold} />
                  </Box>
                </Collapse>
              )}
              {drawerStudent && drawerResults && (
                <Flex align="center" gap={3}>
                  {!showConsolidated && <Box w={10} flexShrink={0} />}
                  <Button
                    size="sm"
                    variant="outline"
                    borderColor={BRAND_BUTTON}
                    color={BRAND_BUTTON}
                    _hover={{ bg: BRAND_BUTTON, color: 'white', borderColor: BRAND_BUTTON }}
                    onClick={handleOpenReportEmail}
                  >
                    Enviar reporte al acudiente
                  </Button>
                </Flex>
              )}
            </Flex>
          </DrawerHeader>
          <DrawerBody pt={4}>
            {/* Con el desglose del consolidado abierto se oculta el listado del periodo
                para no mezclar las dos vistas; al cerrarlo vuelve sin recargar. */}
            <Box display={showConsolidated ? 'none' : undefined}>
            {drawerLoading ? (
              <Flex justify="center" py={12}>
                <Spinner size="lg" />
              </Flex>
            ) : drawerResults && drawerResults.length > 0 ? (
              <Accordion allowMultiple>
                {drawerResults
                  .filter((r) => !r.no_enrollment)
                  .map((result, idx) => {
                    const finalScore = result.final_score?.score ?? 0;
                    const isPassing = Number(finalScore) >= PASSING_SCORE;
                    const rec = result.recuperationQuizEnrollment;
                    const recNote = rec?.note;
                    const recScore =
                      recNote != null && recNote !== '' ? Number(recNote) / 10 : null;
                    const hasSubjAct =
                      Number(result.activities?.completed ?? 0) > 0 ||
                      Number(result.quizzes?.completed ?? 0) > 0 ||
                      result.exam?.completed === true;
                    const showRecoveryBox =
                      recScore != null || (periodClosedView && !isPassing && hasSubjAct);
                    return (
                      <AccordionItem key={idx} borderWidth="1px" borderRadius="lg" mb={3} borderColor="gray.200">
                        <AccordionButton px={4} py={3} _hover={{ bg: 'gray.50' }}>
                          <Flex flex={1} align="center" justify="space-between" mr={2}>
                            <Text fontWeight="medium" fontSize="sm">
                              {result.subject?.name || `Materia ${idx + 1}`}
                            </Text>
                            <HStack spacing={3}>
                              <Badge colorScheme={isPassing ? 'green' : 'red'} variant="subtle" fontSize="xs">
                                Nota: {finalScore}
                              </Badge>
                              <Text fontSize="xs" color="gray.500">
                                Completado: {result.final_score?.completed ?? 0}%
                              </Text>
                            </HStack>
                          </Flex>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                          <SimpleGrid columns={{ base: 2, md: showRecoveryBox ? 4 : 3 }} spacing={3} mb={2}>
                            <Box bg="gray.50" p={3} borderRadius="md">
                              <Text fontSize="xs" color="gray.600">
                                Actividades
                              </Text>
                              <Text fontWeight="bold" fontSize="sm">
                                {result.activities?.completed ?? 0}/{result.activities?.total ?? 0}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                Nota: {result.activities?.score ?? '0.00'}
                              </Text>
                            </Box>
                            <Box bg="gray.50" p={3} borderRadius="md">
                              <Text fontSize="xs" color="gray.600">
                                Quizzes
                              </Text>
                              <Text fontWeight="bold" fontSize="sm">
                                {result.quizzes?.completed ?? 0}/{result.quizzes?.total ?? 0}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                Nota: {result.quizzes?.score ?? '0.00'}
                              </Text>
                            </Box>
                            <Box bg="gray.50" p={3} borderRadius="md">
                              <Text fontSize="xs" color="gray.600">
                                Examen Final
                              </Text>
                              <Text fontWeight="bold" fontSize="sm">
                                {result.exam?.completed ? 'Presentado' : 'Pendiente'}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                Nota: {result.exam?.score ?? '0.00'}
                              </Text>
                            </Box>
                            {showRecoveryBox ? (
                              <Box bg="gray.50" p={3} borderRadius="md">
                                <Text fontSize="xs" color="gray.600">
                                  Recuperación
                                </Text>
                                <Text fontWeight="bold" fontSize="sm">
                                  {recScore != null
                                    ? recScore >= PASSING_SCORE
                                      ? 'Aprobó'
                                      : 'No aprobó'
                                    : 'Pendiente'}
                                </Text>
                                <Text
                                  fontSize="xs"
                                  color={
                                    recScore != null
                                      ? recScore >= PASSING_SCORE
                                        ? 'green.600'
                                        : 'red.600'
                                      : 'orange.600'
                                  }
                                >
                                  {recScore != null
                                    ? `Nota: ${recScore.toFixed(2)}`
                                    : 'Sin intento registrado'}
                                </Text>
                              </Box>
                            ) : null}
                          </SimpleGrid>
                        </AccordionPanel>
                      </AccordionItem>
                    );
                  })}
              </Accordion>
            ) : (
              <Text color="gray.500" textAlign="center" py={8}>
                No se encontraron resultados para este estudiante.
              </Text>
            )}
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Modal isOpen={isBulkNotesOpen} onClose={handleBulkNotesClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {bulkNotesStep === 'confirming' && 'Insertar notas del periodo'}
            {bulkNotesStep === 'processing' && 'Insertando notas…'}
            {bulkNotesStep === 'success' && 'Notas insertadas'}
            {bulkNotesStep === 'error' && 'Error'}
          </ModalHeader>
          <ModalBody>
            {bulkNotesStep === 'confirming' && (
              <Text>
                ¿Deseas insertar/actualizar las notas finales del{' '}
                <strong>{period === 'tercer' ? 'tercer' : period}</strong> periodo para los{' '}
                <strong>{totalStudents}</strong> estudiantes del reporte?
              </Text>
            )}
            {bulkNotesStep === 'processing' && (
              <Flex align="center" gap={3} py={2}>
                <Spinner size="md" />
                <Text>Insertando notas para {totalStudents} estudiantes…</Text>
              </Flex>
            )}
            {bulkNotesStep === 'success' && bulkNotesResult && (
              <Text>
                Se procesaron <strong>{bulkNotesResult.processed}</strong> estudiantes:{' '}
                <strong>{bulkNotesResult.created ?? 0}</strong> creados, <strong>{bulkNotesResult.updated ?? 0}</strong>{' '}
                actualizados.
              </Text>
            )}
            {bulkNotesStep === 'error' && (
              <Text color="red.600">{bulkNotesError || 'Error al insertar las notas.'}</Text>
            )}
          </ModalBody>
          <ModalFooter>
            {bulkNotesStep === 'confirming' && (
              <>
                <Button variant="ghost" mr={3} onClick={handleBulkNotesClose}>
                  Cancelar
                </Button>
                <Button
                  bg={BRAND_BUTTON}
                  color="white"
                  _hover={{ bg: BRAND_BUTTON, opacity: 0.9 }}
                  onClick={handleBulkNotesConfirm}
                >
                  Confirmar
                </Button>
              </>
            )}
            {(bulkNotesStep === 'processing' || bulkNotesStep === 'success' || bulkNotesStep === 'error') && (
              <Button
                bg={BRAND_BUTTON}
                color="white"
                _hover={{ bg: BRAND_BUTTON, opacity: 0.9 }}
                onClick={handleBulkNotesClose}
              >
                Cerrar
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isBulkEmailOpen}
        onClose={bulkEmailStep === 'sending' ? () => { } : handleBulkEmailClose}
        isCentered
        size="lg"
        closeOnOverlayClick={bulkEmailStep !== 'sending'}
        closeOnEsc={bulkEmailStep !== 'sending'}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {bulkEmailStep === 'configuring' &&
              `Enviar correo masivo (${totalStudents} estudiante${totalStudents === 1 ? '' : 's'})`}
            {bulkEmailStep === 'sending' && 'Enviando reportes…'}
            {bulkEmailStep === 'success' && 'Envío completado'}
            {bulkEmailStep === 'error' && 'Error en el envío'}
          </ModalHeader>
          {bulkEmailStep !== 'sending' && <ModalCloseButton />}
          <ModalBody>
            {bulkEmailStep === 'configuring' && (
              <>
                <Box bg="orange.50" borderWidth="1px" borderColor="orange.200" borderRadius="md" p={3} mb={4}>
                  <Text fontSize="sm" color="gray.800">
                    <strong>Atención:</strong> vas a enviar el reporte académico a los{' '}
                    <strong>{totalStudents}</strong> estudiantes que coinciden con los filtros actuales:{' '}
                    {bulkEmailFilterSummary}.
                  </Text>
                  {!bulkEmailIncludeAlreadySent && (
                    <Text fontSize="sm" color="gray.700" mt={2}>
                      Por defecto <strong>no</strong> se vuelve a enviar a quienes ya tienen envío registrado para este
                      mismo semestre y periodo (indicador en la tabla). Marca la opción de abajo si necesitas reenviar
                      también a esos estudiantes.
                    </Text>
                  )}
                </Box>
                <FormControl isRequired mb={3}>
                  <FormLabel fontSize="sm">Asunto del correo</FormLabel>
                  <Input
                    size="sm"
                    value={bulkEmailAsunto}
                    onChange={(e) => setBulkEmailAsunto(e.target.value)}
                    placeholder="Asunto"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Puedes usar <code>{'{{studentName}}'}</code> para insertar el nombre completo del estudiante en el asunto.
                  </Text>
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel fontSize="sm">Mensaje personalizado (inicio)</FormLabel>
                  <Textarea
                    size="sm"
                    rows={6}
                    value={bulkEmailMensajeInicial}
                    onChange={(e) => setBulkEmailMensajeInicial(e.target.value)}
                  />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel fontSize="sm">Mensaje de cierre</FormLabel>
                  <Textarea
                    size="sm"
                    rows={4}
                    value={bulkEmailMensajeFinal}
                    onChange={(e) => setBulkEmailMensajeFinal(e.target.value)}
                  />
                </FormControl>
                <Box bg="gray.100" borderRadius="md" p={3} fontSize="xs" color="gray.600">
                  <Text fontWeight="semibold" mb={2} color="gray.700">
                    Vista previa estructural del correo
                  </Text>
                  <Text>1. Logo / plantilla Klazia</Text>
                  <Text>2. Tu mensaje personalizado (inicio)</Text>
                  <Text>3. Bloque automático: seguimiento académico y detalle por materia de cada estudiante</Text>
                  <Text>4. Mensaje de cierre y datos de contacto</Text>
                </Box>
                <Checkbox
                  isChecked={bulkEmailIncludeAlreadySent}
                  onChange={(e) => setBulkEmailIncludeAlreadySent(e.target.checked)}
                  size="sm"
                  colorScheme="purple"
                  mt={3}
                >
                  Incluir a quienes ya recibieron este reporte (mismo semestre y periodo) — reenvío a todos
                </Checkbox>
              </>
            )}
            {bulkEmailStep === 'sending' && (
              <Flex direction="column" align="center" py={8} gap={4}>
                <Spinner size="xl" color={BRAND_BUTTON} thickness="4px" />
                <Text textAlign="center" color="gray.600">
                  Procesando envíos. Por favor, no cierres esta ventana…
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {totalStudents} correo{totalStudents === 1 ? '' : 's'}
                </Text>
              </Flex>
            )}
            {bulkEmailStep === 'success' && bulkEmailResult && (
              <Text fontSize="sm">
                Enviados: <strong>{bulkEmailResult.sent}</strong>.
                {(bulkEmailResult.alreadySent ?? 0) > 0 && (
                  <>
                    {' '}
                    Omitidos (ya enviados antes): <strong>{bulkEmailResult.alreadySent}</strong>.
                  </>
                )}{' '}
                Sin correo de acudiente: <strong>{bulkEmailResult.noEmail}</strong>. Fallidos:{' '}
                <strong>{bulkEmailResult.failed}</strong>.
              </Text>
            )}
            {bulkEmailStep === 'error' && (
              <Text color="red.600" fontSize="sm">
                {bulkEmailError || 'No se pudo completar el envío.'}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            {bulkEmailStep === 'configuring' && (
              <>
                <Button variant="ghost" mr={3} onClick={handleBulkEmailClose}>
                  Cancelar
                </Button>
                <Button
                  bg={BRAND_BUTTON}
                  color="white"
                  _hover={{ bg: BRAND_BUTTON, opacity: 0.9 }}
                  onClick={handleBulkEmailSend}
                  isDisabled={!bulkEmailAsunto?.trim()}
                >
                  Enviar a {totalStudents}
                </Button>
              </>
            )}
            {(bulkEmailStep === 'success' || bulkEmailStep === 'error') && (
              <Button bg={BRAND_BUTTON} color="white" _hover={{ bg: BRAND_BUTTON, opacity: 0.9 }} onClick={handleBulkEmailClose}>
                Cerrar
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isReportEmailOpen} onClose={handleCloseReportEmail} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh" h="90vh" display="flex" flexDirection="column">
          <ModalHeader>Enviar reporte al acudiente</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" pb={6} flex="1" minH={0} display="flex" flexDirection="column">
            <Flex gap={6} flex="1" minH={0}>
              <Box flex="0 0 42%" overflowY="auto">
                <Text fontWeight="bold" mb={4} fontSize="md">
                  Información de envío
                </Text>
                <FormControl isRequired mb={4}>
                  <FormLabel fontSize="sm">Destinatario</FormLabel>
                  <Input
                    value={reportEmailDestinatario}
                    size="sm"
                    placeholder="correo_del_acudiente@ejemplo.com"
                    onChange={(e) => setReportEmailDestinatario(e.target.value)}
                  />
                  {!reportEmailDestinatario?.trim() && (
                    <Text fontSize="xs" color="red.500" mt={1}>
                      No se encontró correo del acudiente para este estudiante.
                    </Text>
                  )}
                </FormControl>
                <FormControl isRequired mb={4}>
                  <FormLabel fontSize="sm">Asunto</FormLabel>
                  <Input
                    value={reportEmailAsunto}
                    onChange={(e) => {
                      setReportEmailAsunto(e.target.value);
                      scheduleReportEmailPreview();
                    }}
                    size="sm"
                    placeholder="Reporte de Seguimiento Académico - Nombre del estudiante"
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel fontSize="sm">Mensaje inicial</FormLabel>
                  <Textarea
                    value={reportEmailMensajeInicial}
                    onChange={(e) => {
                      setReportEmailMensajeInicial(e.target.value);
                      scheduleReportEmailPreview();
                    }}
                    size="sm"
                    rows={5}
                    placeholder="Escriba el mensaje que irá antes del reporte..."
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel fontSize="sm">Mensaje final</FormLabel>
                  <Textarea
                    value={reportEmailMensajeFinal}
                    onChange={(e) => {
                      setReportEmailMensajeFinal(e.target.value);
                      scheduleReportEmailPreview();
                    }}
                    size="sm"
                    rows={4}
                    placeholder="Escriba el mensaje de cierre que irá después del reporte..."
                  />
                </FormControl>
              </Box>
              <Box
                flex="1"
                minW={0}
                minH={0}
                display="flex"
                flexDirection="column"
                borderLeftWidth="1px"
                borderColor="gray.200"
                pl={6}
              >
                <Text fontWeight="bold" mb={4} fontSize="md">
                  Vista previa
                </Text>
                <Box
                  flex="1"
                  minH={0}
                  borderRadius="lg"
                  bg="gray.100"
                  pt={6}
                  px={6}
                  pb={0}
                  display="flex"
                  flexDirection="column"
                  overflow="hidden"
                >
                  <Box
                    flex="1"
                    minH={0}
                    borderRadius="md"
                    bg="white"
                    boxShadow="sm"
                    overflowY="auto"
                    display="flex"
                    flexDirection="column"
                  >
                    {reportEmailLoadingPreview ? (
                      <Flex justify="center" align="center" flex="1">
                        <Spinner size="lg" />
                      </Flex>
                    ) : reportEmailPreviewHtml ? (
                      <Box flex="1" minH={0} position="relative">
                        <iframe
                          title="Vista previa del correo"
                          srcDoc={reportEmailPreviewHtml}
                          sandbox=""
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                        />
                      </Box>
                    ) : (
                      <Text color="gray.500" fontSize="sm" p={4}>
                        La vista previa se generará al cargar o al editar los mensajes.
                      </Text>
                    )}
                  </Box>
                </Box>
              </Box>
            </Flex>
          </ModalBody>
          <ModalFooter bg="gray.100">
            <Button variant="ghost" mr={3} onClick={handleCloseReportEmail}>
              Cancelar
            </Button>
            <Button
              bg={BRAND_BUTTON}
              color="white"
              _hover={{ bg: BRAND_BUTTON, opacity: 0.9 }}
              _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
              onClick={handleSendReportEmail}
              isLoading={reportEmailLoadingSend}
              isDisabled={!reportEmailAsunto?.trim() || !reportEmailDestinatario || reportEmailLoadingSend}
            >
              Enviar correo
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isDetailExportModalOpen}
        onClose={() => {
          if (detailExportPhase === 'counting' || detailExportPhase === 'generating') return;
          setIsDetailExportModalOpen(false);
        }}
        closeOnOverlayClick={detailExportPhase !== 'counting' && detailExportPhase !== 'generating'}
        closeOnEsc={detailExportPhase !== 'counting' && detailExportPhase !== 'generating'}
        isCentered
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Generando reporte detallado</ModalHeader>
          <ModalCloseButton isDisabled={detailExportPhase === 'counting' || detailExportPhase === 'generating'} />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Se exportan los <strong>mismos estudiantes</strong> que ves en la tabla con los filtros actuales (tipo,
              programa, grupo, estado de matrícula, semestre, colegio). El archivo incluye una hoja por materia (formato
              LOGROS POR PERIODO) según el periodo seleccionado.
            </Text>
            {detailExportModalSummary.length > 0 && (
              <Box as="ul" pl={4} mb={3} fontSize="sm" color="gray.700">
                {detailExportModalSummary.map((line, idx) => (
                  <Text as="li" key={`${idx}-${line}`} mb={1}>
                    {line}
                  </Text>
                ))}
              </Box>
            )}
            {detailExportCount != null && (
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Se incluirán <strong>{detailExportCount}</strong> estudiante
                {detailExportCount === 1 ? '' : 's'} (máximo {detailExportMax} por descarga).
              </Text>
            )}
            <Text fontSize="xs" color="gray.500" mb={4}>
              Puede tardar uno o varios minutos según la cantidad de estudiantes. No cierres esta pestaña; la descarga
              comenzará automáticamente al terminar.
            </Text>
            {detailExportPhase === 'counting' && (
              <Flex align="center" gap={3}>
                <Spinner size="md" color={BRAND_BUTTON} />
                <Text fontSize="sm">Contando estudiantes…</Text>
              </Flex>
            )}
            {detailExportPhase === 'generating' && (
              <Box>
                <Progress size="sm" isIndeterminate colorScheme="purple" borderRadius="md" mb={2} />
                <Text fontSize="sm" color="gray.600">
                  Generando libro Excel…
                </Text>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isCertExportModalOpen}
        onClose={() => {
          if (certExportPhase === 'counting' || certExportPhase === 'generating') return;
          setIsCertExportModalOpen(false);
        }}
        closeOnOverlayClick={certExportPhase !== 'counting' && certExportPhase !== 'generating'}
        closeOnEsc={certExportPhase !== 'counting' && certExportPhase !== 'generating'}
        isCentered
        size={certExportResult ? 'xl' : 'md'}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{certExportResult ? 'Resultado de la descarga de certificados' : 'Generando certificados (PDF)'}</ModalHeader>
          <ModalCloseButton isDisabled={certExportPhase === 'counting' || certExportPhase === 'generating'} />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Se genera <strong>un solo PDF</strong> con los certificados válidos del colegio y programa seleccionados.
              Los estudiantes cuyo certificado no se pueda generar aparecerán en una lista para revisar.
            </Text>
            {detailExportModalSummary.length > 0 && (
              <Box as="ul" pl={4} mb={3} fontSize="sm" color="gray.700">
                {detailExportModalSummary.map((line, idx) => (
                  <Text as="li" key={`cert-${idx}-${line}`} mb={1}>
                    {line}
                  </Text>
                ))}
              </Box>
            )}
            {certExportCount != null && !certExportResult && (
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Se revisarán <strong>{certExportCount}</strong> estudiante
                {certExportCount === 1 ? '' : 's'} (máximo {certExportMax} por descarga).
              </Text>
            )}
            {!certExportResult && <Text fontSize="xs" color="gray.500" mb={4}>
              Puede tardar uno o varios minutos. Mantén esta pestaña abierta; la descarga comenzará al terminar.
            </Text>}
            {certExportResult && (
              <Box>
                <Text mb={3} fontWeight="semibold">
                  {certExportResult.generated} certificados generados de {certExportResult.total} estudiantes.
                  {' '}{certExportResult.skipped.length} omitidos.
                </Text>
                {certExportResult.skipped.length > 0 && (
                  <>
                    <Button size="sm" mb={3} onClick={() => {
                      const lines = certExportResult.skipped.map(student => `${student.studentId}\t${student.name || 'Sin nombre'}\t${student.message}`);
                      const text = ['Estudiantes sin certificado', 'Código\tEstudiante\tMotivo', ...lines].join('\r\n');
                      downloadCertificateFile(new Blob(['\uFEFF', text], { type: 'text/plain;charset=utf-8' }), 'certificados-no-generados.txt');
                    }}>Descargar lista (.txt)</Button>
                    <TableContainer maxH="340px" overflowY="auto">
                      <Table size="sm">
                        <Thead><Tr><Th>Código</Th><Th>Estudiante</Th><Th>Motivo</Th></Tr></Thead>
                        <Tbody>{certExportResult.skipped.map(student => (
                          <Tr key={student.studentId}>
                            <Td>{student.studentId}</Td>
                            <Td whiteSpace="normal">{student.name || 'Sin nombre'}</Td>
                            <Td whiteSpace="normal">{student.message}</Td>
                          </Tr>
                        ))}</Tbody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            )}
            {certExportPhase === 'counting' && (
              <Flex align="center" gap={3}>
                <Spinner size="md" color={BRAND_BUTTON} />
                <Text fontSize="sm">Contando estudiantes…</Text>
              </Flex>
            )}
            {certExportPhase === 'generating' && (
              <Box>
                <Progress size="sm" isIndeterminate colorScheme="purple" borderRadius="md" mb={2} />
                <Text fontSize="sm" color="gray.600">
                  Generando PDF (una página por estudiante)…
                </Text>
              </Box>
            )}
          </ModalBody>
          {certExportResult && <ModalFooter><Button onClick={() => setIsCertExportModalOpen(false)}>Cerrar</Button></ModalFooter>}
        </ModalContent>
      </Modal>
    </Box>
  );
}
